import React, { useCallback, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Popup } from '@atlaskit/popup/popup';
import Toggle from '@atlaskit/toggle/toggle';
import { auth } from '@atlaskit/outbound-auth-flow-client';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useFetch3PAuthentications } from './hooks/use3PAuthentication.ts';
import {
	AUTH_OPTIONS,
	EXTERNAL_PRODUCT_CONNECTION,
	EXTERNAL_PRODUCT_CONNECTION_ICON_URLS,
	type ExternalProductConnection,
} from './__types__/RemoteObject.ts';

// Loom is excluded — it uses a different auth flow and isn't covered by the query.
const AUTH_REQUIRED_SOURCES = new Set<ExternalProductConnection>(AUTH_OPTIONS);

const i18n = defineMessages({
	filterButtonLabel: {
		id: 'space-curation.review-content-suggestions-modal.filter-button-label',
		defaultMessage: 'Filter suggestions by source',
		description:
			'Accessible label for the settings button that opens the suggestions source filter popup',
	},
	showOnlyFrom: {
		id: 'space-curation.review-content-suggestions-modal.filter-show-only-from',
		defaultMessage: 'Show only suggestions from',
		description: 'Heading at the top of the suggestions source filter popup',
	},
	googleDrive: {
		id: 'space-curation.review-content-suggestions-modal.filter-source-google-drive',
		defaultMessage: 'Google Drive',
		description: 'Display name for the Google Drive source in the suggestions filter popup',
	},
	loom: {
		id: 'space-curation.review-content-suggestions-modal.filter-source-loom',
		defaultMessage: 'Loom',
		description: 'Display name for the Loom source in the suggestions filter popup',
	},
	microsoft: {
		id: 'space-curation.review-content-suggestions-modal.filter-source-microsoft',
		defaultMessage: 'Microsoft',
		description: 'Display name for the Microsoft source in the suggestions filter popup',
	},
});

const styles = cssMap({
	popupContent: {
		padding: token('space.075'),
		width: '260px',
	},
	sourceRow: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
	},
	heading: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.200'),
	},
	sourceName: {
		flexGrow: 1,
		minWidth: '0%',
	},
});

type FilterSource = {
	key: ExternalProductConnection;
	iconUrl: string;
	message: (typeof i18n)[keyof typeof i18n];
};

const FILTER_SOURCES: FilterSource[] = [
	{
		key: EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE,
		iconUrl: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE],
		message: i18n.googleDrive,
	},
	{
		key: EXTERNAL_PRODUCT_CONNECTION.LOOM,
		iconUrl: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.LOOM],
		message: i18n.loom,
	},
	{
		key: EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS,
		iconUrl: EXTERNAL_PRODUCT_CONNECTION_ICON_URLS[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT_TEAMS],
		message: i18n.microsoft,
	},
];

export const SuggestionsFilterPopup = () => {
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);
	const [enabledSources, setEnabledSources] = useState<Record<string, boolean>>(() =>
		Object.fromEntries(FILTER_SOURCES.map((source) => [source.key, true])),
	);

	const { cloudId } = useSessionData();
	const { authenticatedServices, unauthenticatedServices, isLoading, refetch } =
		useFetch3PAuthentications({
			products: isOpen ? [...AUTH_REQUIRED_SOURCES] : [],
			cloudId,
		});

	const isSourceAuthenticated = useCallback(
		(key: ExternalProductConnection): boolean =>
			!AUTH_REQUIRED_SOURCES.has(key) || Boolean(authenticatedServices[key]?.isAuthenticated),
		[authenticatedServices],
	);

	const handleToggle = async (key: ExternalProductConnection) => {
		if (isSourceAuthenticated(key)) {
			setEnabledSources((prev) => ({ ...prev, [key]: !prev[key] }));
			return;
		}
		// Unauthenticated: toggling on starts the OAuth flow.
		const authUrl = unauthenticatedServices[key]?.outboundAuthUrl;
		if (!authUrl) {
			return;
		}
		try {
			await auth(authUrl);
			setEnabledSources((prev) => ({ ...prev, [key]: true }));
			await refetch();
		} catch {
			// Cancelled or failed — leave the toggle off.
		}
	};

	const isSourceChecked = useCallback(
		(key: ExternalProductConnection): boolean =>
			isSourceAuthenticated(key) && Boolean(enabledSources[key]),
		[isSourceAuthenticated, enabledSources],
	);

	// Lock the last enabled source so the user can't turn every source off.
	const enabledCount = useMemo(
		() =>
			FILTER_SOURCES.reduce(
				(count, source) => (isSourceChecked(source.key) ? count + 1 : count),
				0,
			),
		[isSourceChecked],
	);

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement="bottom-end"
			zIndex={800}
			content={() => (
				<Box xcss={styles.popupContent} testId="suggestions-filter-popup-content">
					<Stack space="space.0">
						<Box xcss={styles.heading}>
							{/* Text (not Heading) — Heading has no subtlest color option. */}
							<Text size="small" weight="bold" color="color.text.subtlest">
								{formatMessage(i18n.showOnlyFrom)}
							</Text>
						</Box>
						<Stack space="space.0">
							{FILTER_SOURCES.map((source) => {
								const name = formatMessage(source.message);
								const isChecked = isSourceChecked(source.key);
								const isLastEnabled = isChecked && enabledCount === 1;
								return (
									<Flex
										key={source.key}
										alignItems="center"
										justifyContent="space-between"
										gap="space.100"
										xcss={styles.sourceRow}
									>
										<Inline alignBlock="center" space="space.100">
											<img src={source.iconUrl} alt="" aria-hidden="true" width="16" height="16" />
											<Box xcss={styles.sourceName}>
												<Text size="medium">{name}</Text>
											</Box>
										</Inline>
										<Toggle
											label={name}
											isChecked={isChecked}
											isDisabled={
												(AUTH_REQUIRED_SOURCES.has(source.key) && isLoading) || isLastEnabled
											}
											onChange={() => void handleToggle(source.key)}
											testId={`suggestions-filter-toggle-${source.key}`}
										/>
									</Flex>
								);
							})}
						</Stack>
					</Stack>
				</Box>
			)}
			trigger={(triggerProps) => (
				<IconButton
					{...triggerProps}
					icon={CustomizeIcon}
					label={formatMessage(i18n.filterButtonLabel)}
					appearance="subtle"
					spacing="compact"
					isSelected={isOpen}
					onClick={() => setIsOpen(!isOpen)}
					testId="suggestions-filter-button"
				/>
			)}
		/>
	);
};
